import CircularLoader from 'components/CircularLoader';
import { Input } from 'components/ui/input';
import React, { useCallback, useMemo } from 'react';
import { ClassifierData } from 'types/classifiers';
import { JobRoleGroupType } from 'types/jobRoles';
import { HourlyRate } from 'types/pricelists';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';

/**
 * `framed` is the standalone editor: its own heading, a fixed-height scroller sized by `height`, and the
 * Clear-all action. `flow` renders the groups alone and lets the surrounding pane own the heading, the
 * scrolling and Clear all — the case editor stacks the individual-rates ledger under the same scroll.
 */
type Layout = 'framed' | 'flow';

interface Props {
    isValidating?: boolean;
    onChange: (value: HourlyRate[]) => void;
    classifiers: Record<string, ClassifierData>;
    hourlyRates: HourlyRate[];
    height?: number;
    isLoading?: boolean;
    layout?: Layout;
}

function groupBy(list: HourlyRate[], f: (jr: HourlyRate) => string): Record<string, HourlyRate[]> {
    return list
        .toSorted((a, b) => (a.jobRole.orderNum ?? Infinity) - (b.jobRole.orderNum ?? Infinity))
        .reduce((r: Record<string, HourlyRate[]>, v: HourlyRate) => {
            const k = f(v);
            (r[k] || (r[k] = [])).push(v); // Initialize if missing, then push
            return r;
        }, {});
}

export const HourlyRatesEditForm = ({
    isValidating,
    onChange,
    classifiers,
    hourlyRates,
    height,
    isLoading,
    layout = 'framed',
}: Props): JSX.Element => {
    const isFlow = layout === 'flow';
    const jobRoleGroups = useMemo<Record<string, HourlyRate[]>>(
        () => groupBy(hourlyRates, (hourlyRate) => hourlyRate.jobRole.groupNameTypeCode ?? JobRoleGroupType.LEGAL),
        [hourlyRates]
    );

    const onPriceChange = useCallback(
        (hourlyRate: HourlyRate, value: string) => {
            if (new RegExp('^[0-9]*[.,]?([0-9]{0,2})?$').test(value)) {
                const price = value.replace(',', '.');
                onChange(
                    hourlyRates.map((rate) =>
                        rate.jobRole.id === hourlyRate.jobRole.id ? { ...rate, hourlyPrice: price } : rate
                    )
                );
            }
        },
        [hourlyRates, onChange]
    );

    const renderDetailsRow = useCallback(
        (hourlyRate: HourlyRate, key: string): JSX.Element => {
            const fieldValue = hourlyRates.find((rate) => rate.jobRole.id === hourlyRate.jobRole.id)?.hourlyPrice;
            const isRequired = hourlyRate.jobRole.isActive;
            const isError = isValidating && !fieldValue && fieldValue?.toString() !== '0' && isRequired;
            const name = hourlyRate.jobRole.internalName ?? hourlyRate.jobRole.name;
            const inputId = `hourly-rate-${hourlyRate.jobRole.id}`;

            if (isFlow) {
                return (
                    // The right padding reserves the width of the ledger row's remove button, so the role
                    // prices and the individual prices below them share one column edge.
                    <div key={key} className="flex items-center gap-2 py-0.5 pr-9.5">
                        <span className={cn('text-grey-700 flex-1 truncate text-[0.8rem]', isError && 'text-red-600')}>
                            {name}
                            {isRequired ? <span className="text-red-600"> *</span> : null}
                        </span>
                        <Input
                            aria-label={name}
                            aria-invalid={isError}
                            className="bg-paper w-18 flex-none text-right text-base tabular-nums md:text-xs"
                            value={fieldValue ?? ''}
                            onChange={(event) => onPriceChange(hourlyRate, event.target.value)}
                        />
                    </div>
                );
            }

            return (
                <React.Fragment key={key}>
                    <div className="col-span-9 flex flex-col">
                        <label
                            htmlFor={inputId}
                            className="text-body1 text-fg-secondary mt-2 truncate text-left leading-[1.4375em]"
                        >
                            {name}
                            {isRequired ? <span className="text-red-600"> *</span> : null}
                        </label>
                        {isError ? (
                            <div className="new:text-caption self-start text-[0.7rem] text-red-600">
                                {t('validation.required-fields-empty')}
                            </div>
                        ) : null}
                    </div>
                    <div className="col-span-3">
                        <Input
                            id={inputId}
                            aria-invalid={isError}
                            className="bg-paper text-base md:text-xs"
                            value={fieldValue ?? ''}
                            onChange={(event) => onPriceChange(hourlyRate, event.target.value)}
                        />
                    </div>
                </React.Fragment>
            );
        },
        [hourlyRates, isValidating, isFlow, onPriceChange]
    );

    const groupTitle = (group: JobRoleGroupType): JSX.Element => (
        <div
            className={
                isFlow
                    ? 'text-grey-600 new:text-label new:tracking-[0.06em] pt-2.5 pb-1 text-[0.7rem] font-semibold tracking-wider uppercase'
                    : 'col-span-12 font-semibold'
            }
        >
            {getClassifierText(classifiers['JOB_ROLE_GROUP_TYPE'], group)}
        </div>
    );

    const groupRows = (group: JobRoleGroupType): JSX.Element[] =>
        (jobRoleGroups[group] ?? []).map((hourlyRate: HourlyRate, index: number) =>
            renderDetailsRow(hourlyRate, `${group}-${index}`)
        );

    if (isFlow) {
        return (
            <div className={cn('relative', isLoading && 'opacity-50')}>
                {isLoading ? <CircularLoader /> : null}
                {groupTitle(JobRoleGroupType.LEGAL)}
                {groupRows(JobRoleGroupType.LEGAL)}
                {groupTitle(JobRoleGroupType.ADMIN)}
                {groupRows(JobRoleGroupType.ADMIN)}
            </div>
        );
    }

    return (
        <div className="relative">
            {isLoading ? <CircularLoader /> : null}
            <div className="flex h-12.5 items-center pr-5.5">{t('pricelists.hourly-rates')}</div>
            <div
                className={cn('grid grid-cols-12 content-start gap-x-4 gap-y-2 overflow-y-auto pr-5.5', {
                    'opacity-50': isLoading,
                })}
                style={{ height: (height ?? 465) - 85 }}
            >
                {groupTitle(JobRoleGroupType.LEGAL)}
                {groupRows(JobRoleGroupType.LEGAL)}
                {groupTitle(JobRoleGroupType.ADMIN)}
                {groupRows(JobRoleGroupType.ADMIN)}
                <div className="h-2" />
            </div>
            <hr className="mt-0 border-zinc-300/25" />
            <div className="flex justify-end">
                <button
                    type="button"
                    className="font-inherit text-link mb-2 h-8.75 cursor-pointer border-none bg-inherit pr-4 text-sm"
                    onClick={() =>
                        onChange(
                            hourlyRates.map((rate) => ({
                                ...rate,
                                hourlyPrice: null,
                            }))
                        )
                    }
                >
                    {t('pricelists.clear-all-rates')}
                </button>
            </div>
        </div>
    );
};
